import React, { useEffect, useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import Button from '../common/Button';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import { Heading, Text, Typography } from '../common/Typography';
import { getGuestIdentificationSummary } from '../../services/guestService';
import { fetchPlayerUnifiedGameHistory } from '../../services/playerService';
import GameHistoryTable from '../player/GameHistoryTable';
import { getPlayerNameColor } from '../../utils/playerUtils';

const safeFormatDate = (value, pattern) => {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' && value.length === 10
      ? parseISO(value)
      : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, pattern);
  } catch {
    return '—';
  }
};

/**
 * Helps admins identify a guest (e.g. single-name guests) via last session and recent games.
 */
function GuestInfoModal({ isOpen, onClose, guest, clubId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);

  const load = useCallback(async () => {
    if (!guest?.id || !clubId) return;
    setLoading(true);
    setError(null);
    setSummary(null);
    setGameHistory([]);
    try {
      const [data, games] = await Promise.all([
        getGuestIdentificationSummary(guest.id, clubId),
        fetchPlayerUnifiedGameHistory(guest.id, clubId, null, 25, 0),
      ]);
      setSummary(data);
      setGameHistory(games || []);
    } catch (err) {
      console.error('[GuestInfoModal]', err);
      setError(err.message || 'Could not load guest activity');
    } finally {
      setLoading(false);
    }
  }, [guest?.id, clubId]);

  useEffect(() => {
    if (isOpen && guest?.id && clubId) {
      load();
    }
  }, [isOpen, guest?.id, clubId, load]);

  if (!isOpen || !guest) return null;

  const g = summary?.guest || guest;
  const displayName = [g.first_name, g.last_name].filter(Boolean).join(' ') || 'Guest';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="large" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border-medium px-4 py-3 sm:px-6">
          <div className="min-w-0 pr-2">
            <Heading level={2} className="text-lg sm:text-xl">
              Guest activity
            </Heading>
            <Text className="mt-1 text-sm text-text-secondary">
              Use session history to confirm who this guest is when names are incomplete or ambiguous.
            </Text>
          </div>
          <ModalCloseButton onClose={onClose} className="sticky top-0" />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y px-4 py-4 sm:px-6">
          <div className="mb-6 flex items-start gap-4 rounded-lg border border-border-medium bg-surface-subtle p-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-info-light">
              <span className="text-lg font-medium text-info-dark">
                {(g.first_name?.[0] || '?').toUpperCase()}
                {(g.last_name?.[0] || '').toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <Typography variant="strong" className={`text-lg ${getPlayerNameColor(g.gender)}`}>
                {displayName}
              </Typography>
              <div className="mt-1 flex flex-wrap gap-2 text-sm text-text-secondary">
                <span>
                  Added{' '}
                  {safeFormatDate(g.created_at, 'MMM d, yyyy')}
                </span>
                {g.guest_invited_email && (
                  <span className="text-text-muted">
                    · {g.guest_invited_email}
                  </span>
                )}
              </div>
            </div>
          </div>

          {loading && (
            <Typography variant="body" className="text-text-secondary py-8 text-center">
              Loading activity…
            </Typography>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-danger/30 bg-danger-light p-4 text-danger-dark text-sm">
              {error}
            </div>
          )}

          {!loading && !error && summary && (
            <>
              <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                <div className="rounded-lg border border-border-medium bg-surface p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Sessions</div>
                  <div className="mt-1 text-lg font-semibold text-text-primary">
                    {summary.totals.totalSessions}
                  </div>
                </div>
                <div className="rounded-lg border border-border-medium bg-surface p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Games (ELO)</div>
                  <div className="mt-1 text-lg font-semibold text-text-primary">
                    {summary.totals.totalGamesPlayed}
                  </div>
                </div>
                <div className="rounded-lg border border-border-medium bg-surface p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Mixed</div>
                  <div className="mt-1 text-sm font-semibold text-text-primary">
                    {summary.totals.totalMixedGames}
                  </div>
                </div>
                <div className="rounded-lg border border-border-medium bg-surface p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Levels</div>
                  <div className="mt-1 text-sm font-semibold text-text-primary">
                    {summary.totals.totalLevelsGames}
                  </div>
                </div>
                <div className="rounded-lg border border-border-medium bg-surface p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Singles</div>
                  <div className="mt-1 text-sm font-semibold text-text-primary">
                    {summary.totals.totalSinglesGames}
                  </div>
                </div>
                <div className="rounded-lg border border-border-medium bg-surface p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Unbal.</div>
                  <div className="mt-1 text-sm font-semibold text-text-primary">
                    {summary.totals.totalUnbalancedGames}
                  </div>
                </div>
              </div>

              <div className="mb-6 rounded-lg border border-border-medium bg-surface p-4">
                <Heading level={4} className="mb-2 text-base">
                  Most recent session at this club
                </Heading>
                {summary.lastSession ? (
                  <div className="space-y-1 text-sm">
                    <Typography variant="strong" className="text-text-primary">
                      {summary.lastSession.name}
                    </Typography>
                    <Text className="text-text-secondary">
                      {safeFormatDate(summary.lastSession.sessionDate, 'EEE, MMM d, yyyy')}
                      {summary.lastSession.startTime
                        ? ` · ${summary.lastSession.startTime.slice(0, 5)}`
                        : ''}
                    </Text>
                    <Text className="text-text-muted text-xs capitalize">
                      Status: {summary.lastSession.sessionStatus || '—'}
                      {' · '}
                      {summary.lastSession.gamesPlayedAtSession} game
                      {summary.lastSession.gamesPlayedAtSession !== 1 ? 's' : ''} with this guest
                    </Text>
                  </div>
                ) : (
                  <Text className="text-text-secondary text-sm">
                    No completed session games recorded for this club yet.
                  </Text>
                )}
              </div>

              <div>
                <Heading level={4} className="mb-3 text-base">
                  Recent games (newest first)
                </Heading>
                <GameHistoryTable
                  games={gameHistory}
                  pageSize={10}
                  hideFilters
                  hideHeading
                  clubId={clubId}
                  subjectPlayerLabel={displayName}
                />
                {gameHistory.length >= 25 && (
                  <Text className="mt-2 text-xs text-text-muted">
                    Showing the 25 most recent games at this club.
                  </Text>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-border-medium px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:px-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}

export default GuestInfoModal;
